import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Plus, ExternalLink, Loader2 } from "lucide-react";
import { useWorkspaceBilling, useCanManageBilling } from "@/hooks/use-workspace-billing";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { getFreshSession } from "@/lib/auth-utils";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { createCheckout as checkoutLink } from "@/services/billing.service";
import { armAutoReloadPromo } from "@/components/Billing/AutoReloadPromoDialog";

/** Stripe Checkout saves the card with a first top-up of this much (the server's minimum). */
const FIRST_TOP_UP = 20;

export function AddPaymentMethodDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  // Use WORKSPACE billing, not user billing profile
  const { data: workspaceBilling } = useWorkspaceBilling();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { currentWorkspaceId } = useWorkspace();
  const canManage = useCanManageBilling();

  const createCheckout = useMutation({
    mutationFn: async () => {
      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }

      if (!currentWorkspaceId) throw new Error("No workspace selected");

      return checkoutLink(currentWorkspaceId, FIRST_TOP_UP);
    },
    onSuccess: ({ checkout_url }) => {
      // Remembered here, not on the way back: this is a full page navigation,
      // and the billing cache that knows they had no card does not survive it.
      armAutoReloadPromo(currentWorkspaceId);
      window.location.href = checkout_url;
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to create checkout",
        description: error.message,
      });
      setLoading(false);
    },
  });

  // `loading` outlives the request: the button stays busy while the browser leaves for Stripe.
  const handleAddPaymentMethod = () => {
    setLoading(true);
    createCheckout.mutate();
  };

  // Check WORKSPACE payment method status
  if (workspaceBilling?.payment_method_added) {
    return (
      <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
        <CreditCard className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          Payment method added and ready to use for this workspace
        </AlertDescription>
      </Alert>
    );
  }

  // Adding a card is an owner/admin action; the server refuses it to other roles.
  if (!canManage) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="default">
          <Plus className="w-4 h-4 mr-2" />
          Add Payment Method
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Payment Method & Top Up</DialogTitle>
          <DialogDescription>
            You'll be redirected to Stripe's secure checkout to add your payment method and make an initial $20 top-up to your wallet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Minimum initial top-up is $20. Your card is saved securely with Stripe for future top-ups. Auto-reload can be turned on afterwards.
            </AlertDescription>
          </Alert>
          
          <Button 
            onClick={handleAddPaymentMethod} 
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redirecting to Stripe...
              </>
            ) : (
              <>
                Continue to Stripe Checkout
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          
          <p className="text-xs text-muted-foreground text-center">
            You'll be redirected to Stripe's secure payment page
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
